import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useMemo } from "react";

import Button from "~/components/buttons/Button/Button";
import type {
	StaffingUnlock,
	UpdateStaffingUnlocksPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { useStaffingUnlockSelection } from "~/modules/billing/components/InvoiceDetailsView/components/useStaffingUnlockSelection.ts";

type Props = {
	busy?: boolean;
	onCancel: () => void;
	onSubmit: (payloads: UpdateStaffingUnlocksPayload[]) => void;
	staffingUnlocks: StaffingUnlock[];
	/** A function gets the number of selected staffings and the number of all staffings. */
	submitLabel: (selectedCount: number, totalCount: number) => string;
};

type ListProps = {
	onToggle: (staffingId: string) => void;
	selection: Set<string>;
	staffingUnlocks: StaffingUnlock[];
};

export function StaffingUnlockList({ onToggle, selection, staffingUnlocks }: ListProps) {
	const sortedStaffingUnlocks = useMemo(
		() => [...staffingUnlocks].sort((a, b) => a.userName.localeCompare(b.userName)),
		[staffingUnlocks],
	);

	// About 30 staffings are the worst case: the list scrolls.
	return <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1 text-sm">
		{sortedStaffingUnlocks.map(staffingUnlock => {
			const isSelected = selection.has(staffingUnlock.staffingId);
			return <li key={staffingUnlock.staffingId}>
				<button type="button"
						aria-pressed={isSelected}
						className={clsx(
							"flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left font-medium shadow-sm transition-colors",
							isSelected
								? "border-green-300 bg-green-50 text-gray-900 hover:bg-green-100"
								: "border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
						)}
						onClick={() => onToggle(staffingUnlock.staffingId)}>
					{isSelected
						? <LockOpenIcon className="size-5 shrink-0 text-green-600" />
						: <LockClosedIcon className="size-5 shrink-0 text-red-600" />}
					{staffingUnlock.userName}
				</button>
			</li>;
		})}
	</ul>;
}

export function StaffingUnlockForm({ busy, onCancel, onSubmit, staffingUnlocks, submitLabel }: Props) {
	const { payloads, selectAll, selectNone, selection, toggleStaffing } = useStaffingUnlockSelection(staffingUnlocks);
	const allSelected = selection.size === staffingUnlocks.length;

	return <div className="space-y-3 text-sm">
		<div className="flex justify-end">
			<Button type="button"
					theme="white"
					size="sm"
					rounded="md"
					className="border border-gray-300"
					onClick={allSelected ? selectNone : selectAll}>
				{allSelected ? "Alle abwählen" : "Alle auswählen"}
			</Button>
		</div>
		<StaffingUnlockList staffingUnlocks={staffingUnlocks}
							selection={selection}
							onToggle={toggleStaffing} />
		<div className="flex items-center justify-between gap-3 pt-3">
			<Button type="button"
					theme="primary"
					size="md"
					disabled={busy || payloads.length === 0}
					onClick={() => onSubmit(payloads)}>
				{submitLabel(selection.size, staffingUnlocks.length)}
			</Button>
			<Button type="button"
					theme="none"
					size="md"
					onClick={onCancel}>
				Abbrechen
			</Button>
		</div>
	</div>;
}
